import { useEffect, useRef } from "react";
import { makeGlobe, makeWave, makeFocus } from "./engine";
import { cx, prefersReducedMotion } from "./util";

/**
 * Live ASCII illustration: a rotating shaded globe with a mint knowledge graph,
 * or a slow dither wave. Three stacked <pre> layers (faint / muted / mint).
 * Writes textContent directly each frame (no React re-render), ~30fps,
 * pauses offscreen, single still frame under prefers-reduced-motion.
 * In globe mode, hovering eases the field toward a "boosted" state: the view
 * zooms in slightly and denser knowledge-graph connections fade in, so it
 * reads as expanding into the graph rather than just spinning faster.
 */
export default function AsciiField({ mode = "globe", cols = 72, rows = 34, nodes = 46, speed = 1, phase = 0.6, label, className, style }) {
  const wrap = useRef(null), r0 = useRef(null), r1 = useRef(null), r2 = useRef(null), r3 = useRef(null);
  const boost = useRef(0), boostTarget = useRef(0);

  useEffect(() => {
    const frame = mode === "wave" ? makeWave(cols, rows) : mode === "focus" ? makeFocus(cols, rows) : makeGlobe(cols, rows, nodes);
    const n = cols * rows;
    const out = [new Array(n), new Array(n), new Array(n), new Array(n).fill(" ")];
    const start = performance.now();
    let raf = 0, last = 0, visible = true, io;

    const join = (a) => {
      let s = "";
      for (let y = 0; y < rows; y++) { s += a.slice(y * cols, (y + 1) * cols).join(""); if (y < rows - 1) s += "\n"; }
      return s;
    };
    const draw = (now) => {
      boost.current += (boostTarget.current - boost.current) * 0.12;
      frame(((now - start) / 1000) * speed + phase, out, boost.current);
      if (r0.current) r0.current.textContent = join(out[0]);
      if (r1.current) r1.current.textContent = join(out[1]);
      if (r2.current) r2.current.textContent = join(out[2]);
      if (r3.current) r3.current.textContent = join(out[3]);
    };
    const loop = (now) => {
      raf = requestAnimationFrame(loop);
      if (!visible || now - last < 33) return;
      last = now; draw(now);
    };

    draw(performance.now());
    if (!prefersReducedMotion() && speed > 0) {
      raf = requestAnimationFrame(loop);
      if ("IntersectionObserver" in window && wrap.current) {
        io = new IntersectionObserver(([e]) => { visible = e.isIntersecting; });
        io.observe(wrap.current);
      }
    }
    return () => { cancelAnimationFrame(raf); io && io.disconnect(); };
  }, [mode, cols, rows, nodes, speed, phase]);

  useEffect(() => {
    if (mode !== "globe" || prefersReducedMotion()) return undefined;
    const el = wrap.current;
    if (!el) return undefined;
    const enter = () => { boostTarget.current = 1; };
    const leave = () => { boostTarget.current = 0; };
    el.addEventListener("pointerenter", enter);
    el.addEventListener("pointerleave", leave);
    return () => {
      el.removeEventListener("pointerenter", enter);
      el.removeEventListener("pointerleave", leave);
    };
  }, [mode]);

  return (
    <div ref={wrap} className={cx("gd-ascii", className)} style={style} role="img"
      aria-label={label || (mode === "globe" ? "A rotating ASCII globe of connected knowledge nodes" : mode === "focus" ? "An ASCII field of scattered dots tightening into a single sharp ring" : "An ASCII wave pattern")}>
      <pre ref={r0} className="gd-l-faint" aria-hidden="true" />
      <pre ref={r1} className="gd-l-muted" aria-hidden="true" />
      <pre ref={r2} className="gd-l-mint" aria-hidden="true" />
      <pre ref={r3} className="gd-l-mintink" aria-hidden="true" />
    </div>
  );
}
